import React from 'react';
import PropTypes, { number, string, object, bool, oneOfType, instanceOf } from 'prop-types';
import { injectIntl } from '../../util/reactIntl';
import CurrencySymbol from '../CurrencySymbol/CurrencySymbol';
import { formatMoney, convertMoneyToNumber, formatAmountOnly } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './CurrencyAmount.module.css';

const { Money } = sdkTypes;

/**
 * CurrencyAmount component displays amount with Saudi Riyal symbol
 * Format: [Symbol] 123.45
 * 
 * @param {Money|number|string} amount - Amount to display (Money object, number, or string)
 * @param {string} currency - Currency code (default: 'SAR')
 * @param {object} intl - React Intl object
 * @param {number} symbolSize - Size of currency symbol in pixels (default: 20)
 * @param {string} symbolColor - Color of the currency symbol (default: '#231f20' - black)
 * @param {boolean} showSymbol - Whether to show the symbol (default: true)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {string} className - Additional CSS classes
 */
const CurrencyAmountComponent = ({ 
  amount, 
  currency = 'SAR', 
  intl, 
  symbolSize = 20,
  symbolColor = '#231f20',
  showSymbol = true,
  decimals = 2,
  className 
}) => {
  let displayValue;

  try {
    // Handle Money object
    if (amount instanceof Money) {
      const numValue = convertMoneyToNumber(amount);
      displayValue = formatAmountOnly(intl, numValue, decimals);
    } 
    // Handle number or string
    else {
      displayValue = formatAmountOnly(intl, amount, decimals);
    }
  } catch (error) {
    console.error('Error formatting currency amount:', error);
    displayValue = String(amount || '0.00');
  }

  return (
    <span className={`${css.currencyAmount} ${className || ''}`}>
      {showSymbol && (
        <>
          <CurrencySymbol size={symbolSize} color={symbolColor} className={css.symbol} />
          {/* <span className={css.spacer}>&nbsp;</span> */}
        </>
      )}
      <span className={css.amount}>{displayValue}</span>
    </span>
  );
};

CurrencyAmountComponent.propTypes = {
  amount: oneOfType([
    instanceOf(Money),
    number,
    string,
  ]).isRequired,
  currency: string,
  intl: object.isRequired,
  symbolSize: number,
  symbolColor: string,
  showSymbol: bool,
  decimals: number,
  className: string,
};

const CurrencyAmount = injectIntl(CurrencyAmountComponent);
export default CurrencyAmount;
